import { createMemo, For, onMount, Show } from 'solid-js'
import { Character } from '/common/types/library'
import { AppSchema } from '/common/types'
import { getAssetUrl, setComponentPageTitle } from '/web/shared/util'
import { characterStore, userStore } from '/web/store'
import { Meta } from '@solidjs/meta'
import { Title } from '@solidjs/meta'

type AlphabetGroup = {
  letter: string
  characters: Character[]
}

export default function CharacterDirectory(props: { show: boolean; close: () => void }) {
  const chars = characterStore((s) => ({
    list: s.characters.list,
    map: s.characters.list.reduce<Record<string, AppSchema.Character>>(
      (prev, curr) => Object.assign(prev, { [curr._id]: curr }),
      {}
    ),
    loaded: s.characters.loaded,
  }))

  onMount(() => {
    if (!chars.loaded) {
      characterStore.getCharacters()
    }
  })

  // Group characters by first letter
  const groupedCharacters = createMemo(() => {
    if (!chars.loaded) return []

    const groups: AlphabetGroup[] = []
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')

    alphabet.forEach((letter) => {
      const matchingChars = chars.list
        .filter((char) => char.name.toUpperCase().startsWith(letter))
        .sort((a, b) => {
          // First sort by name
          const nameCompare = a.name.localeCompare(b.name)
          // If names are equal, sort by realName
          if (nameCompare === 0) {
            return (a.realName || '').localeCompare(b.realName || '')
          }
          return nameCompare
        })

      if (matchingChars.length > 0) {
        groups.push({ letter, characters: matchingChars })
      }
    })

    return groups
  })

  return (
    <Show when={props.show}>
      <Title>Character Directory</Title>
      <Meta
        name="description"
        content="Browse our complete directory of AI characters sorted alphabetically"
      />
      <div
        class="directory-overlay fixed inset-0 z-50 overflow-y-auto bg-main p-4"
        itemscope
        itemtype="https://schema.org/CollectionPage"
        role="dialog"
        aria-modal="true"
        aria-label="Character Directory"
        onClick={props.close}
      >
        <button
          onClick={props.close}
          class="hover:text-accent fixed right-4 top-4 z-[51] p-2 text-2xl transition-colors"
          aria-label="Close directory"
        >
          ✕
        </button>

        {/* Meta tags and breadcrumbs */}
        <meta
          name="description"
          content="Browse our complete directory of AI characters sorted alphabetically"
        />
        <div itemscope itemtype="https://schema.org/BreadcrumbList" class="hidden">
          <div itemprop="itemListElement" itemscope itemtype="https://schema.org/ListItem">
            <meta itemprop="position" content="1" />
            <meta itemprop="name" content="Home" />
            <link itemprop="item" href="/" />
          </div>
          <div itemprop="itemListElement" itemscope itemtype="https://schema.org/ListItem">
            <meta itemprop="position" content="2" />
            <meta itemprop="name" content="Character Directory" />
            <link itemprop="item" href="/directory" />
          </div>
        </div>

        <div class="container relative mx-auto max-w-7xl">
          <h1
            class="mb-6 text-3xl font-bold"
            itemprop="name headline"
            onClick={(e) => e.stopPropagation()}
          >
            Character Directory
          </h1>

          <nav class="mb-6 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
            <For each={groupedCharacters()}>
              {(group) => (
                <a href={`#section-${group.letter}`} class="rounded bg-primary px-3 py-1 text-sm">
                  {group.letter}
                </a>
              )}
            </For>
          </nav>

          <div
            class="columns-1 gap-6 md:columns-2 lg:columns-3"
            itemprop="mainEntity"
            itemscope
            itemtype="https://schema.org/ItemList"
            onClick={(e) => e.stopPropagation()}
          >
            <meta itemprop="numberOfItems" content={chars.list.length.toString()} />
            <meta itemprop="itemListOrder" content="Ascending" />

            <For each={groupedCharacters()}>
              {(group) => (
                <section id={`section-${group.letter}`} class="mb-6 break-inside-avoid-column">
                  <h2 class="mb-3 text-2xl font-bold">{group.letter}</h2>
                  <div class="grid gap-3">
                    <For each={group.characters}>
                      {(char) => (
                        <a
                          href={`/model/${char.categorySlug}/${char.slug}`}
                          onClick={() => userStore.directoryModal(false)}
                          class="flex items-center gap-4 rounded bg-secondary/25 p-3 transition-colors hover:bg-secondary/30"
                          itemprop="itemListElement"
                          itemscope
                          itemtype="https://schema.org/Person"
                        >
                          <img
                            src={getAssetUrl(char.avatar!)}
                            alt={char.name}
                            class="h-14 w-14 rounded-full object-cover"
                            itemprop="image"
                          />
                          <div>
                            <h3 class="font-bold" itemprop="name">
                              {char.name}
                            </h3>
                            <p class="text-sm text-gray-300" itemprop="additionalName">
                              {char.realName}
                            </p>
                            <meta itemprop="description" content={char.description} />
                            <meta
                              itemprop="url"
                              content={`/model/${char.categorySlug}/${char.slug}`}
                            />
                            <meta
                              itemprop="position"
                              content={(group.characters.indexOf(char) + 1).toString()}
                            />
                          </div>
                        </a>
                      )}
                    </For>
                  </div>
                </section>
              )}
            </For>
          </div>
        </div>
      </div>
    </Show>
  )
}
